import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import { Amount, NovaQr, AddressUnit, BankTransferCard, closeUnlessAdminConsumed } from './modalKit';
import BankLogo from '../components/BankLogo';
import {
    ShieldCheck, ShieldAlert, X, Check, Landmark, Info, ArrowUpFromLine,
} from 'lucide-react';

/**
 * NovaVerificationModal — не платёжный лист, а процедура проверки счёта.
 * Биржа перед выплатой сверяет, что счёт действительно принадлежит клиенту:
 * пользователь делает проверочный платёж с тех же реквизитов, что и депозит,
 * платёж возвращается — совпадение суммы и отправителя подтверждает доступ.
 *
 * История рассказана так, чтобы с одного взгляда было ясно: это не блок
 * и деньги не пропали. Заголовок называет аккаунт, шаг 1 — «паспорт счёта»
 * с зелёным статусом «готово к отправке», шаг 2 — проверочный платёж
 * в акцентном индиго (защита, не потеря), шаг 3 — куда отправить.
 * Пунктирная рельса и рамка — язык временного, возвратного действия.
 *
 * Тексты, переменные и логика владельца (App.js) не меняются — компонент
 * получает готовые значения и рендерится порталом внутрь корня .nova
 */
export default function NovaVerificationModal({
    isClosing,
    onClose,
    safeT,
    title,
    detailsTitle,
    // счёт под проверкой
    network,
    bankIconSrc,
    accountLabel,
    accountValue,
    amountText,
    amountReadyLabel,
    unverifiedLabel,
    // верификация
    instructionHtml,
    verificationNote,
    paymentRequiredLabel,
    feeAmount,
    // оплата
    paymentTitle,
    dataAsOfText,
    addressDisplay,
    networkInfoHtml,
    qrValue,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
    // банковский режим оплаты
    bankMode,
    payAccountLabel,
    payBankName,
    payAccountNumber,
    payFields,
    payBankIconSrc,
    bankPaymentTitle,
    // действия
    cancelLabel,
    paidLabel,
}) {
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!host) return null;

    const stripColon = (text) => String(text || '').replace(/[::]\s*$/, '');

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${s.sheetNarrow} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Статус-строка: чей аккаунт проверяем. Щит акцентный —
                    защитная проверка, не ошибка */}
                <header className={s.head}>
                    <span className={`${s.headIcon} ${s.headIconAccent}`}>
                        <ShieldCheck size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <h2 className={s.headTitle}>{title}</h2>
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label={cancelLabel}
                    >
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    {/* Вводная: зачем всё это — существующий instruction */}
                    <p
                        className={s.lede}
                        dangerouslySetInnerHTML={{ __html: instructionHtml }}
                    />

                    <div className={s.steps}>
                        {/* ── Шаг 1: паспорт счёта — банк, номер и сумма,
                            уже собранная к отправке. Зелёный статус говорит:
                            деньги целы и ждут только подтверждения */}
                        <div className={s.step}>
                            <span className={s.stepDot}>1</span>
                            <div className={s.stepMain}>
                                <span className={s.stepTitle}>{stripColon(detailsTitle)}</span>
                                <div className={s.acctRow}>
                                    <span className={s.acctBankIcon}>
                                        {bankIconSrc
                                            ? <BankLogo src={bankIconSrc} alt="" />
                                            : <Landmark size={19} strokeWidth={1.8} aria-hidden="true" />}
                                    </span>
                                    <div className={s.acctMain}>
                                        <span className={s.acctBankRow}>
                                            <span className={s.acctBank}>{network}</span>
                                            {/* Причина процедуры — счёт ещё не верифицирован;
                                                тот же ключ, что у статуса в хедере */}
                                            <span className={s.acctBadge}>
                                                <ShieldAlert size={10} strokeWidth={2.4} aria-hidden="true" />
                                                {unverifiedLabel}
                                            </span>
                                        </span>
                                        <span className={s.acctNumber}>
                                            <span className={s.acctLabel}>{stripColon(accountLabel)}</span>
                                            {accountValue}
                                        </span>
                                    </div>
                                    <span className={s.acctDivider} aria-hidden="true" />
                                    <div className={s.acctAmount}>
                                        <Amount text={amountText} tone="out" />
                                        {/* Бейдж под суммой, на всю её ширину вместе с валютой */}
                                        <span className={`${s.eyebrow} ${s.eyebrowOut} ${s.acctReady}`}>
                                            <ArrowUpFromLine size={11} strokeWidth={2.4} aria-hidden="true" />
                                            {amountReadyLabel}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Шаг 2: проверочный платёж — талон-эталон на всю
                            ширину, под ним пояснение, почему платить нужно
                            с тех же данных, что и депозит */}
                        <div className={s.step}>
                            <span className={s.stepDot}>2</span>
                            <div className={s.stepMain}>
                                <span className={s.stepTitle}>
                                    <span className={`${s.pulse} ${s.pulseVerify}`} aria-hidden="true" />
                                    {stripColon(paymentRequiredLabel)}
                                </span>
                                <div className={`${s.verifyPlate} ${s.verifyPlateWide}`}>
                                    <Amount text={feeAmount} tone="verify" />
                                </div>
                                <p className={s.verifyCaption}>
                                    <Info size={13} strokeWidth={2.1} aria-hidden="true" />
                                    <span>{verificationNote}</span>
                                </p>
                            </div>
                        </div>

                        {/* ── Шаг 3: куда отправить. В заголовке — отметка
                            актуальности реквизитов на дату и время */}
                        <div className={s.step}>
                            <span className={s.stepDot}>3</span>
                            <div className={s.stepMain}>
                                <span className={s.stepTitle}>
                                    {stripColon(paymentTitle)}
                                    {dataAsOfText && (
                                        <span className={s.stepTitleNote}>({dataAsOfText})</span>
                                    )}
                                </span>
                                {/* Ряд «QR + адрес + плашка сети» вынесен из-под
                                    рельсы шага на всю ширину листа (депозит как
                                    в остальных модалах) — иначе плашке не хватало
                                    ширины и она ломалась на две строки */}
                                <div className={`${s.deposit} ${s.depositFill} ${s.depositLeftText} ${s.depositBleed} ${bankMode ? s.depositBank : ''}`}>
                                    {bankMode ? (
                                        <BankTransferCard
                                            safeT={safeT}
                                            paymentTitle={bankPaymentTitle}
                                            bankName={payBankName}
                                            bankLogoSrc={payBankIconSrc}
                                            accountLabel={payAccountLabel}
                                            accountNumber={payAccountNumber}
                                            fields={payFields}
                                            isCopied={isCopied}
                                            onCopy={onCopy}
                                        />
                                    ) : (
                                        <>
                                            <div className={s.qrFrame}>
                                                <NovaQr value={qrValue} size={96} />
                                            </div>
                                            <AddressUnit
                                                safeT={safeT}
                                                addressDisplay={addressDisplay}
                                                networkInfoHtml={networkInfoHtml}
                                                canHideAddress={canHideAddress}
                                                isAddressHidden={isAddressHidden}
                                                onToggleAddressHidden={onToggleAddressHidden}
                                                isCopied={isCopied}
                                                onCopy={onCopy}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Действия */}
                <footer className={s.foot}>
                    {/* Единственное действие «Я оплатил» на всю ширину */}
                    <div className={`${s.footActions} ${s.footActionsSolo}`}>
                        <button type="button" className={s.primaryBtn}>
                            <Check size={15} strokeWidth={2.6} aria-hidden="true" />
                            {paidLabel}
                        </button>
                    </div>
                </footer>
            </div>
        </div>,
        host,
    );
}
